/**
 * useContractForm — F-003.2 Phase 1 (IMPL-P1-003) + F-005 Phase 5 (IMPL-P5-002).
 *
 * Hook condiviso (F-003.2 wizard + F-003.3 form denso + F-005 correzione).
 *
 * Responsabilità:
 *  1. Istanza RHF con zodResolver(contractDraftSchema) — contractDraftSchema
 *     è contractFullBaseSchema.deepPartial(), quindi accetta partial save.
 *  2. Autosave: ogni change del form schedula un PUT (create) o PATCH (correct)
 *     dopo 1.5s. Il timer è cancellato se il form cambia di nuovo prima che scada.
 *  3. validateStep(stepIndex): chiama form.trigger() solo con i campi del passo
 *     corrente — non tocca i passi futuri.
 *  4. draftStatus: state machine 'idle' | 'saving' | 'saved' | 'error'.
 *  5. F-005: in `mode='correct'`, idratazione one-shot del form via
 *     `form.reset(initialData)` su mount, e autosave verso
 *     `saveCorrezioneBozza(praticaId, draft)`.
 *
 * Decisioni architetturali:
 *  - Q1: Sempre crea nuova BOZZA su mount in mode='create' — "Riprendi bozza"
 *    non è in scope di F-005 (solo correzione di pratiche BLOCCATE).
 *  - Q2: tipologia null → default wizard (gestito in NuovaPraticaPage).
 *  - F-005 backward compat: la signature accetta sia `(praticaId: string)` (legacy
 *    F-003.x) sia `(opts: UseContractFormOpts)` (F-005). Gli overload TS
 *    discriminano sulla forma del primo argomento.
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import { useForm } from 'react-hook-form';
import type { DefaultValues, FieldValues, UseFormReturn } from 'react-hook-form';
import type { ZodType } from 'zod';
import { zodResolver } from '@hookform/resolvers/zod';
import { contractDraftSchema } from '@app/shared';
import type { ContractDraft } from '@app/shared';
import { updateBozza } from '../lib/pratica-client';
import { saveCorrezioneBozza } from '../../pratiche-cliente/lib/cliente-pratiche-client';
import { STEP_FIELDS } from '../lib/step-fields';

export type DraftStatus = 'idle' | 'saving' | 'saved' | 'error';

export type ContractFormMode = 'create' | 'correct';

/**
 * F-004: `T` è il tipo di form-values effettivo. Default `ContractDraft`
 * (comportamento F-003/F-005 invariato). Gli adempimenti, che iniettano
 * `adempimentoDraftSchema`, specificano `T = AdempimentoDraft` così che il
 * `form` restituito accetti i field-path della variante (es. il discriminante
 * `adempimentoType`) in `setValue`/`trigger`. Puramente additivo: nessun caller
 * esistente specifica il generic, quindi tutti restano su `ContractDraft`.
 */
export interface UseContractFormOpts<T extends FieldValues = ContractDraft> {
  praticaId: string;
  /** F-005: 'correct' instrada l'autosave verso `saveCorrezioneBozza`. Default 'create'. */
  mode?: ContractFormMode;
  /** F-005: dati iniziali da idratare nel form (one-shot, su mount). */
  initialData?: Partial<T>;
  /**
   * F-005: lista field-path contestati dall'operatore. Restituita as-is
   * dall'hook così che `NuovaPraticaPage` possa iniettarla in
   * `<ErrorFieldsProvider>`.
   */
  errorFields?: string[];
  /**
   * F-004: schema Zod (deepPartial/draft) che guida `zodResolver`. Additivo —
   * default `contractDraftSchema` (comportamento F-003/F-005 invariato). Gli
   * adempimenti passano `adempimentoDraftSchema` per validazione per-variante.
   *
   * Tipizzato `ZodType<T>` così che `T` (il tipo dei form-values) sia INFERITO
   * dallo schema iniettato: quando un caller passa `adempimentoDraftSchema`
   * (output `AdempimentoDraft`), il `form` restituito è `UseFormReturn<AdempimentoDraft>`
   * e `setValue('adempimentoType', …)` type-checka. Senza schema, `T` resta
   * il default `ContractDraft` — nessun caller F-003/F-005 cambia comportamento.
   */
  schema?: ZodType<T>;
  /**
   * F-004: mappa step-index → field-path per la validazione parziale
   * (`validateStep`). Additivo — default `STEP_FIELDS` (comportamento
   * F-003/F-005 invariato). Gli adempimenti passano una entry di
   * `ADEMPIMENTO_STEP_FIELDS` per la variante corrente.
   */
  stepFields?: ReadonlyArray<ReadonlyArray<string>>;
}

export interface UseContractFormResult<T extends FieldValues = ContractDraft> {
  form: UseFormReturn<T>;
  draftStatus: DraftStatus;
  validateStep: (stepIndex: number) => Promise<boolean>;
  praticaId: string;
  /** F-005: re-export degli `errorFields` passati in input (default []). */
  errorFields: string[];
}

// ---------------------------------------------------------------------------
// Overload signatures: backward compat con i caller F-003.x (positional string)
// + nuova firma F-005 (opts object).
// ---------------------------------------------------------------------------
export function useContractForm(praticaId: string): UseContractFormResult<ContractDraft>;
export function useContractForm<T extends FieldValues = ContractDraft>(
  opts: UseContractFormOpts<T>,
): UseContractFormResult<T>;
export function useContractForm<T extends FieldValues = ContractDraft>(
  arg: string | UseContractFormOpts<T>,
): UseContractFormResult<T> {
  const opts: UseContractFormOpts<T> =
    typeof arg === 'string' ? ({ praticaId: arg } as UseContractFormOpts<T>) : arg;
  const {
    praticaId,
    mode = 'create',
    initialData,
    errorFields,
    // F-004: injection point. Default = comportamento F-003/F-005 odierno.
    schema = contractDraftSchema,
    stepFields = STEP_FIELDS,
  } = opts;

  const form = useForm<T>({
    resolver: zodResolver(schema),
    mode: 'onBlur',
    defaultValues: {} as DefaultValues<T>,
  });

  const [draftStatus, setDraftStatus] = useState<DraftStatus>('idle');
  const isFirstRender = useRef(true);

  // F-005: idratazione one-shot in mode='correct'. Usa un ref guard per
  // evitare reset infiniti se `initialData` referenzialmente cambia (tipico
  // con TanStack Query refetch).
  const hydratedRef = useRef(false);
  useEffect(() => {
    if (!initialData || hydratedRef.current) return;
    form.reset(initialData as Parameters<typeof form.reset>[0]);
    hydratedRef.current = true;
    // L'autosave deve restare in idle subito dopo la hydration: il primo
    // ciclo `watched` post-reset NON deve triggerare un PATCH (vedi
    // isFirstRender guard nell'autosave effect).
  }, [initialData, form]);

  // Watch tutte le modifiche al form per triggerare l'autosave
  const watched = form.watch();

  useEffect(() => {
    // Skip autosave al primo render (evita PUT/PATCH su mount)
    if (isFirstRender.current) {
      isFirstRender.current = false;
      return;
    }

    setDraftStatus('saving');

    const timer = setTimeout(async () => {
      try {
        if (mode === 'correct') {
          await saveCorrezioneBozza(
            praticaId,
            watched as unknown as Record<string, unknown>,
          );
        } else {
          await updateBozza(praticaId, watched as unknown as ContractDraft);
        }
        setDraftStatus('saved');
      } catch {
        setDraftStatus('error');
      }
    }, 1500);

    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [JSON.stringify(watched), praticaId, mode]);
  // JSON.stringify stabilizza la dependency su un valore primitivo.
  // Alternativa: usare un ref per tracked dirty fields — documentare trade-off.

  const validateStep = useCallback(
    async (stepIndex: number): Promise<boolean> => {
      const fields = stepFields[stepIndex];
      if (!fields || fields.length === 0) return true;
      // form.trigger accetta string[] — il type cast è sicuro
      return form.trigger(fields as Parameters<typeof form.trigger>[0]);
    },
    [form, stepFields],
  );

  return {
    form,
    draftStatus,
    validateStep,
    praticaId,
    errorFields: errorFields ?? [],
  };
}
